import React, { useCallback, useEffect, useState } from 'react';
import { useAnalytics } from '../../../hooks/useAnalytics';
import clsx from 'clsx';
import { SILVER_EVENTS } from './constants';
import KYCButton from '../../Onboarding/KYCButton';
import Input from '../../../components/Fields/Input';
import useGetPartnerProfile from '../../../hooks/useGetPartnerProfile';
import {
  fiveDecimalNumberWithoutRounding,
  fourDecimalNumberWithoutRounding,
  getRupeeWithDecimal,
  twoDecimalNumberWithoutRounding,
} from '../../../utils/numbers';
import SelectClient from '../SelectClient';
import { Checkbox, Dialog, DialogContent, DialogTitle, Skeleton, Typography } from '@mui/material';
import ViewDetails from './ViewDetails';
import { CloseOutlinedIcon } from '../../../assets/mui';
import toast from 'react-hot-toast';
import {
  generateMetalPaymentLink,
  getMetalLivePrice,
  getPartnerMetalCommission,
  useMetalTransaction,
} from '../useDealDetails';
import { TimerComponent } from './components/TimerComponent';
import ClientDrawer from '../../Clients/ClientDrawer/ClientDrawer';
import { useLocation } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info'; // Importing from Material UI (you can use your own icons)
import RocketIcon from '@mui/icons-material/Rocket';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MessageCycler from './components/MessageCycler';
import { baseColors } from '../../../assets/theme';
import SellMetalOtpDialog from '../components/SellMetalOtpDialog';
import SipOptions, { SipFrequency } from '../DigitalGold/components/SipOptions';
import useUserSubscription from '../../../hooks/useUserSubscription';
import { formatMonthlyDate } from './SipUtils';

const messages = [
  {
    text: 'If the time expires while you complete the payment, you will receive silver based on the updated price.',
    icon: <InfoIcon className="text-slate-700" style={{ width: '14px', height: '14px' }} />,
  },
  {
    text: 'Current live price gets updated every 5 Minutes.',
    icon: <RocketIcon className="text-slate-700" style={{ width: '14px', height: '14px' }} />,
  },
  {
    text: 'The buying and selling prices may fluctuate based on live market conditions.',
    icon: <LocalOfferIcon className="text-slate-700" style={{ width: '14px', height: '14px' }} />,
  },
  {
    text: 'The purchased Silver can be sold only after 2 days of purchase',
    icon: <InfoIcon className="text-slate-700" style={{ width: '14px', height: '14px' }} />,
  },
];

const DigitalSilver = () => {
  const { track } = useAnalytics();
  const location = useLocation();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const tabValue = queryParams.get('tab');

  const [plan, setPlan] = useState('BUY_SILVER');

  const [isInvestmentLoading, setIsInvestmentLoading] = useState(false);
  const [userPaymentLink, setUserPaymentLink] = useState(null);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isSipPayment, setIsSipPayment] = useState(false);
  const [sipPaymentData, setSipPaymentData] = useState<{
    frequency: SipFrequency;
    amount: number;
    selectedDate?: Date | null;
    selectedDay?: string;
  } | null>(null);
  const [isSellPaymentOpen, setIsSellPaymentOpen] = useState(false);
  const [client, setClient] = useState<any>(null);
  const [metalLivePrice, setMetalLivePrice] = useState<any>(null);
  const [metalLivePriceLoading, setMetalLivePriceLoading] = useState(false);
  const [partnerCommission, setPartnerCommission] = useState<any>(null);
  const [partnerCommissionLoading, setPartnerCommissionLoading] = useState(false);
  const [isCheckboxSelected, setIsCheckboxSelected] = useState(true);
  const [fetchClients, setFetchClients] = useState(false);
  const { data: partner } = useGetPartnerProfile();
  const [selectedSipFrequency, setSelectedSipFrequency] = useState<SipFrequency | null>(null);
  const [showSipClientError, setShowSipClientError] = useState(false);
  const [sipAmount, setSipAmount] = useState(0);
  const [sipIsGramSelected, setSipIsGramSelected] = useState(false);
  const [sipSelectedGram, setSipSelectedGram] = useState(0);

  const handleFrequencyChange = (frequency: SipFrequency | null) => {
    const previousFrequency = selectedSipFrequency;
    setSelectedSipFrequency(frequency);
    if (frequency !== previousFrequency) {
      setSipAmount(0);
    }
  };
  const [existingSipData, setExistingSipData] = useState<
    Record<SipFrequency, { amount: number; startDate: string } | null>
  >({
    DAILY: null,
    WEEKLY: null,
    MONTHLY: null,
  });

  const partnerId = partner?.data?.profile?.publicId;
  const clientId = client?.clientPublicId;

  // Fetch user subscriptions for SILVER
  const { data: silverSubscriptions } = useUserSubscription(clientId, 'SILVER_SAVING');

  const [sipStatuses, setSipStatuses] = useState<Record<SipFrequency, 'ACTIVE' | 'PAUSED' | null>>({
    DAILY: null,
    WEEKLY: null,
    MONTHLY: null,
  });

  useEffect(() => {
    if (silverSubscriptions?.subscriptions && Array.isArray(silverSubscriptions.subscriptions)) {
      const statuses: Record<SipFrequency, 'ACTIVE' | 'PAUSED' | null> = {
        DAILY: null,
        WEEKLY: null,
        MONTHLY: null,
      };
      const sipData: Record<SipFrequency, { amount: number; startDate: string } | null> = {
        DAILY: null,
        WEEKLY: null,
        MONTHLY: null,
      };

      silverSubscriptions.subscriptions.forEach((subscription) => {
        const frequency = subscription.subscriptionFrequency as SipFrequency;
        if (
          subscription.subscriptionState === 'ACTIVE' ||
          subscription.subscriptionState === 'PAUSED'
        ) {
          statuses[frequency] = subscription.subscriptionState as 'ACTIVE' | 'PAUSED';
          sipData[frequency] = { amount: subscription.amount, startDate: subscription.startDate };
        }
      });
      setSipStatuses(statuses);
      setExistingSipData(sipData);
    } else {
      // Reset to null if no subscriptions
      setSipStatuses({
        DAILY: null,
        WEEKLY: null,
        MONTHLY: null,
      });
      setExistingSipData({
        DAILY: null,
        WEEKLY: null,
        MONTHLY: null,
      });
    }
  }, [silverSubscriptions]);

  const [isGramSelected, setIsGramSelected] = useState(true); // State to track whether gram is selected
  const [selectedGram, setSelectedGram] = useState(1);
  const [selectedAmount, setSelectedAmount] = useState(0); // what user enters also will become our amountPayable
  const [gstAmount, setGstAmount] = useState(0); // livePrice + 3% GST per gm
  const [totalAmountWithoutGst, setTotalAmountWithoutGst] = useState(0);
  const [sellMaxSilver, setSellMaxSilver] = useState(false);
  const {
    data: transactionList,
    isLoading: transactionLoading,
    refetch: transactionRefetch,
  } = useMetalTransaction();

  const MAX_BUY_INVESTMENT_AMOUNT = 5000000;
  const MAX_SELL_INVESTMENT_AMOUNT = 1000000;
  const GST_RATE = 3;

  const fetchLivePrice = async () => {
    setMetalLivePrice(null);
    setMetalLivePriceLoading(true);
    await getMetalLivePrice()
      .then((resp) => {
        setMetalLivePrice(resp);
        setGstAmount(resp?.data?.silverBuyPrice + (resp?.data?.silverBuyPrice * GST_RATE) / 100);
      })
      .finally(() => {
        setMetalLivePriceLoading(false);
      });
  };

  const fetchPartnerCommission = useCallback(
    async (amount?: number) => {
      setPartnerCommission(null);
      setPartnerCommissionLoading(true);
      const commissionAmount = amount !== undefined ? amount : selectedAmount;
      if (!commissionAmount || commissionAmount === 0) {
        setPartnerCommissionLoading(false);
        return;
      }
      await getPartnerMetalCommission({
        amount: Number(commissionAmount),
        metal: 'SILVER',
      })
        .then((resp) => {
          setPartnerCommission(resp);
        })
        .finally(() => {
          setPartnerCommissionLoading(false);
        });
    },
    [selectedAmount],
  );

  useEffect(() => {
    if (tabValue === 'SILVER') {
      fetchLivePrice();
    }
  }, [tabValue]);

  const enforceInvestmentLimit = useCallback(() => {
    if (plan !== 'BUY_SILVER' && plan !== 'SELL_SILVER') {
      return;
    }

    const buyPrice = Number(metalLivePrice?.data?.silverBuyPrice) || 0;
    const sellPrice = Number(metalLivePrice?.data?.silverSellPrice) || 0;
    const pricePerGram = plan === 'BUY_SILVER' ? buyPrice + (buyPrice * GST_RATE) / 100 : sellPrice;

    if (pricePerGram <= 0) {
      return;
    }

    let gramValue = isGramSelected ? Number(selectedGram) : Number(selectedAmount) / pricePerGram;

    if (Number.isNaN(gramValue)) {
      return;
    }

    const amountValue = gramValue * pricePerGram;
    const limit = plan === 'BUY_SILVER' ? MAX_BUY_INVESTMENT_AMOUNT : MAX_SELL_INVESTMENT_AMOUNT;
    const shouldClamp = amountValue > limit;

    const finalAmount = shouldClamp ? limit : amountValue;
    const finalGram = shouldClamp ? limit / pricePerGram : gramValue;

    if (shouldClamp) {
      toast.dismiss();
      toast.error(
        plan === 'BUY_SILVER' ? 'Maximum amount is 50 Lakh' : 'Maximum amount is 10 Lakh',
        {
          duration: 2000,
          position: 'top-center',
        },
      );
    }

    const amountFormatted = twoDecimalNumberWithoutRounding(finalAmount);
    const gramFormatted =
      plan === 'BUY_SILVER'
        ? fourDecimalNumberWithoutRounding(finalGram)
        : fiveDecimalNumberWithoutRounding(finalGram);

    if (Number(selectedAmount) !== Number(amountFormatted)) {
      setSelectedAmount(amountFormatted);
    }

    if (!isGramSelected || shouldClamp) {
      if (selectedGram !== gramFormatted) {
        setSelectedGram(gramFormatted);
      }
    }

    if (plan === 'BUY_SILVER') {
      const totalWithoutGst = twoDecimalNumberWithoutRounding(finalGram * buyPrice);
      setGstAmount(amountFormatted);
      setTotalAmountWithoutGst(totalWithoutGst);
    }
  }, [
    GST_RATE,
    MAX_BUY_INVESTMENT_AMOUNT,
    MAX_SELL_INVESTMENT_AMOUNT,
    isGramSelected,
    metalLivePrice?.data?.silverBuyPrice,
    metalLivePrice?.data?.silverSellPrice,
    plan,
    selectedAmount,
    selectedGram,
  ]);

  useEffect(() => {
    enforceInvestmentLimit();
  }, [enforceInvestmentLimit]);

  useEffect(() => {
    enforceInvestmentLimit();
    // only call Partner commission api if both amount and gram are selected
    if (totalAmountWithoutGst) {
      fetchPartnerCommission();
    }
  }, [
    enforceInvestmentLimit,
    fetchPartnerCommission,
    metalLivePrice?.data?.silverBuyPrice,
    metalLivePrice?.data?.silverSellPrice,
    plan,
    selectedAmount,
    selectedGram,
    sellMaxSilver,
    totalAmountWithoutGst,
  ]);

  const handleSwap = () => {
    setIsGramSelected(!isGramSelected);
  };

  const handleSipSwap = () => {
    setSipIsGramSelected(!sipIsGramSelected);
  };

  const handleSipGramChange = (gram: number) => {
    setSipSelectedGram(gram);
    if (plan === 'START_SIP' && metalLivePrice?.data?.silverBuyPrice) {
      const buyPrice = metalLivePrice.data.silverBuyPrice;
      const priceWithGst = buyPrice + (buyPrice * GST_RATE) / 100;
      const calculatedAmount = twoDecimalNumberWithoutRounding(gram * priceWithGst);
      setSipAmount(calculatedAmount);
      setGstAmount(calculatedAmount);
    }
  };

  const handleSipAmountChange = (amount: number) => {
    setSipAmount(amount);
    if (plan === 'START_SIP' && metalLivePrice?.data?.silverBuyPrice) {
      const buyPrice = metalLivePrice.data.silverBuyPrice;
      const priceWithGst = buyPrice + (buyPrice * GST_RATE) / 100;
      const calculatedGram = fourDecimalNumberWithoutRounding(amount / priceWithGst);
      setSipSelectedGram(calculatedGram);
      setGstAmount(amount);
    }
  };

  const handleSellDialogClose = useCallback(() => {
    setIsSellPaymentOpen(false);
    setClient(null);
  }, []);

  const handleSellSuccess = useCallback(() => {
    setFetchClients(true);
    transactionRefetch();
    setSelectedGram(0);
    setSelectedAmount(0);
    setSellMaxSilver(false);
  }, [setFetchClients, transactionRefetch, setSelectedGram, setSelectedAmount, setSellMaxSilver]);

  useEffect(() => {
    if (client?.metalBlocked) {
      toast.dismiss();
      toast.error('Investment for the selected Clients are blocked, please contact RM', {
        duration: 2000,
        position: 'top-center',
      });
    }
  }, [client]);

  const handleInputChange = (event) => {
    const value = event.target.value;
    toast.dismiss();
    setSellMaxSilver(false);

    if (isNaN(value)) {
      toast.error('only numbers allowed', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }

    if (!isGramSelected && value.includes('.') && value.split('.')[1].length > 2) {
      toast.error('For Amount only up to 2 decimal places are allowed', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }
    if (isGramSelected && value.includes('.') && value.split('.')[1].length > 4) {
      toast.error('For Grams only up to 4 decimal places are allowed', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }

    if (!isGramSelected && Number(value) > MAX_BUY_INVESTMENT_AMOUNT && plan === 'BUY_SILVER') {
      toast.error('Maximum amount is 50Lakh', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }

    if (!isGramSelected && Number(value) > MAX_SELL_INVESTMENT_AMOUNT && plan === 'SELL_SILVER') {
      toast.error('Maximum amount is 10Lakh', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }

    if (isGramSelected && Number(value) > 500 && plan === 'BUY_SILVER') {
      toast.error('Please input amount if you want to add more', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }
    if (isGramSelected && Number(value) > 150 && plan === 'SELL_SILVER') {
      toast.error('Please input amount if you want to add more', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }

    if (isGramSelected) {
      setSelectedGram(value);
      if (plan === 'BUY_SILVER') {
        setSelectedAmount(
          twoDecimalNumberWithoutRounding(
            value *
              (metalLivePrice?.data?.silverBuyPrice +
                (metalLivePrice?.data?.silverBuyPrice * 3) / 100),
          ),
        );
      } else if (plan === 'SELL_SILVER') {
        setSelectedAmount(
          twoDecimalNumberWithoutRounding(value * metalLivePrice?.data?.silverSellPrice),
        );
      }
    } else {
      setSelectedAmount(value);
      if (plan === 'BUY_SILVER') {
        setSelectedGram(
          fourDecimalNumberWithoutRounding(
            value /
              (metalLivePrice?.data?.silverBuyPrice +
                (metalLivePrice?.data?.silverBuyPrice * 3) / 100),
          ),
        );
      } else if (plan === 'SELL_SILVER') {
        setSelectedGram(
          fiveDecimalNumberWithoutRounding(value / metalLivePrice?.data?.silverSellPrice),
        );
      }
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsInvestmentLoading(true);
    setFetchClients(false);
    if (!client) {
      toast.error('Please select a client', {
        duration: 2000,
        position: 'top-center',
      });
      setIsInvestmentLoading(false);
      return;
    }

    if (isCheckboxSelected === false) {
      toast.error('Please agree to the terms and conditions', {
        duration: 2000,
        position: 'top-center',
      });
      return;
    }

    if (selectedAmount === 0 || selectedGram === 0) {
      toast.error('Please enter the amount', {
        duration: 2000,
        position: 'top-center',
      });
      setIsInvestmentLoading(false);
      return;
    }
    if (selectedAmount < 100 && !sellMaxSilver) {
      toast.error('Please select minimum 100rs.', {
        duration: 2000,
        position: 'top-center',
      });
      setIsInvestmentLoading(false);
      return;
    }
    if (plan === 'SELL_SILVER') {
      if (client?.silverInGms < selectedGram) {
        toast.error('Client does not have enough silver to sell', {
          duration: 2000,
          position: 'top-center',
        });
        setIsInvestmentLoading(false);
        return;
      }

      if (!metalLivePrice?.data?.blockId || !metalLivePrice?.data?.silverSellPrice) {
        toast.error('Unable to fetch live price. Please refresh and try again.', {
          duration: 2000,
          position: 'top-center',
        });
        setIsInvestmentLoading(false);
        return;
      }

      setIsSellPaymentOpen(true);
      setIsInvestmentLoading(false);
      return;
    }

    if (selectedAmount && clientId && partnerId) {
      generateMetalPaymentLink({
        clientPublicId: clientId,
        price: metalLivePrice?.data?.silverBuyPrice,
        blockId: metalLivePrice?.data?.blockId,
        quantity: selectedGram,
        amount: totalAmountWithoutGst,
        metal: 'SILVER',
        amountPayable: gstAmount,
      })
        .then((res) => {
          if (res) {
            setUserPaymentLink(res?.data);
            setIsPaymentOpen(true);
          }
        })
        .catch((err) => {
          toast.error('Error generating payment link', {
            duration: 2000,
            position: 'top-center',
          });
        })
        .finally(() => {
          track({
            eventName: SILVER_EVENTS.GENERATE_PAYMENT_LINK,
            eventProperties: {
              clientId: clientId,
              partnerId: partnerId,
              amount: selectedAmount,
              gram: selectedGram,
              metal: 'SILVER',
            },
          });
        });
    }
    setIsInvestmentLoading(false);
  };

  const handlePaymentClose = () => {
    setIsPaymentOpen(false);
    setUserPaymentLink(null);
    setIsSipPayment(false);
    setSipPaymentData(null);
  };

  const handleSipGeneratePaymentLink = (
    authLink?: string,
    errorMessage?: string,
    firstChargeDate?: string,
    scheduleDetails?: { selectedDay?: string; selectedDate?: Date | null },
  ) => {
    if (authLink) {
      const resolvedSelectedDay =
        selectedSipFrequency === 'WEEKLY'
          ? scheduleDetails?.selectedDay ||
            (firstChargeDate
              ? new Date(firstChargeDate).toLocaleString('en-US', { weekday: 'long' })
              : new Date().toLocaleString('en-US', { weekday: 'long' }))
          : undefined;
      const resolvedSelectedDate =
        selectedSipFrequency === 'MONTHLY'
          ? scheduleDetails?.selectedDate ||
            (firstChargeDate ? new Date(firstChargeDate) : new Date())
          : undefined;

      setUserPaymentLink(authLink);
      setIsSipPayment(true);
      setSipPaymentData({
        frequency: selectedSipFrequency!,
        amount: sipAmount,
        selectedDay: resolvedSelectedDay,
        selectedDate: resolvedSelectedDate,
      });

      setIsPaymentOpen(true);
    } else {
      toast.error(errorMessage || 'Error generating payment link. Please try again.', {
        duration: 2000,
        position: 'top-center',
      });
    }
  };

  const handleEditSip = (frequency: SipFrequency, newAmount: number) => {
    // TODO: Implement edit SIP API call
    setExistingSipData((prev) => ({
      ...prev,
      [frequency]: { amount: newAmount },
    }));

    setSipAmount(newAmount);
  };

  const handleStopSip = (
    frequency: SipFrequency,
    period?: '7' | '10' | '15' | 'PERMANENT' | '1' | '2' | '3',
  ) => {
    // TODO: Implement stop SIP API call with period
    // Note: Toast messages are handled in StopSipPeriodModal component

    if (period === 'PERMANENT') {
      setExistingSipData((prev) => ({
        ...prev,
        [frequency]: null,
      }));
    }

    if (selectedSipFrequency === frequency) {
      setSelectedSipFrequency(null);
    }
  };

  useEffect(() => {
    if (selectedAmount && selectedGram) {
      fetchPartnerCommission();
    }
  }, [fetchPartnerCommission, selectedAmount, selectedGram, plan]);

  // Fetch commission when SIP amount changes (with debounce)
  useEffect(() => {
    if (plan !== 'START_SIP' || sipAmount === 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchPartnerCommission(sipAmount);
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timeoutId);
  }, [fetchPartnerCommission, sipAmount, plan]);

  useEffect(() => {
    if (!metalLivePrice?.data?.expiresAt) {
      return;
    }

    const expiresAtTime = new Date(metalLivePrice.data.expiresAt).getTime();
    const currentTime = Date.now();
    const timeLeft = expiresAtTime - currentTime;

    const refreshData = () => {
      setIsSellPaymentOpen(false);
      fetchLivePrice();
      if (selectedAmount && selectedGram) {
        fetchPartnerCommission();
      }
      enforceInvestmentLimit();
    };

    if (timeLeft > 0) {
      const timer = setTimeout(refreshData, timeLeft);
      return () => clearTimeout(timer);
    }

    refreshData();
  }, [
    enforceInvestmentLimit,
    fetchPartnerCommission,
    metalLivePrice?.data?.expiresAt,
    selectedAmount,
    selectedGram,
  ]);
  return (
    <div className="my-6 max-w-7xl mx-auto">
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-8 xl:gap-16">
        {/* left hand section */}
        <div className="col-span-3">
          {tabValue === 'SILVER' && (
            <ViewDetails
              metalLivePrice={metalLivePrice}
              metalLivePriceLoading={metalLivePriceLoading}
              transactionList={transactionList}
              transactionLoading={transactionLoading}
              transactionRefetch={transactionRefetch}
            />
          )}
        </div>
        {/* right hand section */}
        <form className="col-span-2" onSubmit={onSubmit}>
          <div className="mb-6">
            <div className="flex gap-3">
              <button
                type="button"
                className={clsx(' rounded-full py-2 px-4 font-semibold text-xs', {
                  'bg-slate-600 text-white': plan === 'BUY_SILVER',
                  'bg-slate-600/15 text-slate-600': plan !== 'BUY_SILVER',
                })}
                onClick={() => {
                  setPlan('BUY_SILVER');
                  setShowSipClientError(false);
                  setSelectedSipFrequency(null);
                  setSipAmount(0);
                  setSipSelectedGram(0);
                  setSipIsGramSelected(false);
                  setClient(null);
                  setSellMaxSilver(false);
                  setSelectedGram(0);
                  setSelectedAmount(0);
                  setGstAmount(0);
                  // setIsGramSelected(true);
                  track({ eventName: SILVER_EVENTS.BUY_SILVER });
                }}
              >
                Buy Silver
              </button>
              <button
                type="button"
                className={clsx(' rounded-full py-2 px-4 font-semibold text-xs', {
                  'bg-slate-600 text-white': plan === 'SELL_SILVER',
                  'bg-slate-600/15 text-slate-600': plan !== 'SELL_SILVER',
                })}
                onClick={() => {
                  setPlan('SELL_SILVER');
                  setShowSipClientError(false);
                  setSelectedSipFrequency(null);
                  setSipAmount(0);
                  setSipSelectedGram(0);
                  setSipIsGramSelected(false);
                  setClient(null);
                  setSellMaxSilver(false);
                  setSelectedGram(0);
                  setSelectedAmount(0);
                  setGstAmount(0);
                  // setIsGramSelected(true);
                  track({ eventName: SILVER_EVENTS.SELL_SILVER });
                }}
              >
                Sell Silver
              </button>
              <button
                type="button"
                className={clsx(' rounded-full py-2 px-4 font-semibold text-xs', {
                  'bg-slate-600 text-white': plan === 'START_SIP',
                  'bg-slate-600/15 text-slate-600': plan !== 'START_SIP',
                })}
                onClick={() => {
                  setPlan('START_SIP');
                  setShowSipClientError(false);
                  setSelectedSipFrequency(null);
                  setSipAmount(0);
                  setSipSelectedGram(0);
                  setSipIsGramSelected(false);
                  setClient(null);
                  setSellMaxSilver(false);
                  setSelectedGram(0);
                  setSelectedAmount(0);
                  setGstAmount(0);
                  track({ eventName: SILVER_EVENTS.BUY_SILVER });
                }}
              >
                Start SIP
              </button>
            </div>
          </div>
          <div className="flex flex-col">
            {plan === 'START_SIP' ? (
              <SipOptions
                client={client}
                setClient={setClient}
                fetchClients={fetchClients}
                selectedFrequency={selectedSipFrequency}
                onSelectFrequency={handleFrequencyChange}
                showClientError={showSipClientError}
                onMissingClient={() => setShowSipClientError(true)}
                onClearError={() => setShowSipClientError(false)}
                isTermsChecked={isCheckboxSelected}
                onToggleTerms={() => setIsCheckboxSelected(!isCheckboxSelected)}
                sipAmount={sipAmount}
                onSipAmountChange={handleSipAmountChange}
                buyingPrice={gstAmount}
                commission={partnerCommission?.data?.earnedIncomeForInvestment}
                commissionPercentage={0.5}
                metalLivePrice={metalLivePrice}
                metalLivePriceLoading={metalLivePriceLoading}
                isGramSelected={sipIsGramSelected}
                onSwap={handleSipSwap}
                selectedGram={sipSelectedGram}
                onGramChange={handleSipGramChange}
                gstAmount={gstAmount}
                onGeneratePaymentLink={handleSipGeneratePaymentLink}
                sipStatuses={sipStatuses}
                existingSipData={existingSipData}
                onEditSip={(frequency, newAmount) => handleEditSip(frequency, newAmount)}
                onStopSip={handleStopSip}
                metalType="SILVER"
                priceFields={{ buyPrice: 'silverBuyPrice', sellPrice: 'silverSellPrice' }}
                showSilver={true}
              />
            ) : plan === 'BUY_SILVER' ? (
              <div className="border border-stone-300 rounded-lg">
                {/* Order Investment Input */}
                <div className="border-b relative border-b-stone-300 text-slate-600 overflow-hidden h-24 bg-white rounded-t-lg">
                  <label className="absolute z-10 top-4 left-3 tracking-wider uppercase text-xs font-semibold text-black/40">
                    Investment {isGramSelected ? 'Quantity' : 'Amount'}
                  </label>

                  <div className="flex items-center justify-between absolute z-10 top-10 left-2">
                    {/* Input field */}
                    <Input
                      value={isGramSelected ? selectedGram : selectedAmount}
                      onChange={handleInputChange}
                      prefixIcon={
                        <span className="text-black/40 font-medium text-xl pt-3">
                          {isGramSelected ? 'gms' : '₹'}
                        </span>
                      }
                      onFocus={(e) => {
                        e.target.select();
                      }}
                      longPrefix={isGramSelected}
                      type="text"
                      placeholder={''}
                      className="font-semibold text-2xl placeholder:text-stone-400 pt-6 border-none rounded-b-none"
                      containerClassName="border-none rounded-b-none !shadow-none "
                    />
                    {/* Display calculated amount */}
                    <div className="flex items-center mr-4">
                      <span className="text-sm font-medium min-w-[80px] mr-2">
                        {metalLivePriceLoading ? (
                          <Skeleton width={100} animation="wave" />
                        ) : isGramSelected ? (
                          gstAmount ? (
                            `${getRupeeWithDecimal.format(gstAmount)}`
                          ) : (
                            '-'
                          )
                        ) : selectedGram ? (
                          `${selectedGram} gms`
                        ) : (
                          '-'
                        )}
                      </span>

                      {/* Swap button */}
                      <button
                        onClick={handleSwap}
                        className="text-green-800 underline text-xs font-semibold"
                        style={{
                          cursor: 'pointer',
                        }}
                        type="button"
                        // disabled={selectedAmount === 0 || selectedGram === 0}
                      >
                        Swap
                      </button>
                    </div>
                  </div>

                  <div className="h-fit w-fit border border-stone-300 rounded-full text-red-700 absolute z-10 top-2 right-3 inset-y-0 text-xs flex px-2 py-[3px]">
                    Price valid for{' '}
                    {metalLivePrice?.data?.expiresAt ? (
                      <Typography
                        sx={{
                          fontSize: 'inherit',
                          fontWeight: 'inherit',
                        }}
                      >
                        <TimerComponent expiresAt={metalLivePrice?.data?.expiresAt} />
                      </Typography>
                    ) : (
                      <Skeleton animation="wave" width="50px" sx={{ ml: 1 }} />
                    )}
                  </div>
                </div>

                <div className="p-4 md:p-5 border-b border-b-stone-300 flex items-center gap-4 text-slate-600">
                  <h3 className="text-sm min-w-36">Live Price</h3>
                  <span className="text-lg font-medium line-clamp-1">
                    {metalLivePriceLoading ? (
                      <Skeleton width={100} animation="wave" />
                    ) : metalLivePrice?.data ? (
                      getRupeeWithDecimal.format(metalLivePrice?.data?.silverBuyPrice)
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
                <div className="p-4 md:p-5 border-b border-b-stone-300 flex items-center gap-4 text-slate-600">
                  <h3 className="text-sm min-w-36">GST</h3>
                  <span className="text-lg font-medium line-clamp-1">3%</span>
                </div>
                <div className="p-4 md:p-5 border-b border-b-stone-300 flex items-center gap-4 text-slate-600">
                  <h3 className="text-sm min-w-36">Buying Price</h3>
                  <span className="text-lg font-medium">
                    <span className="text-lg font-medium">
                      {metalLivePriceLoading ? (
                        <Skeleton width={100} animation="wave" />
                      ) : metalLivePrice?.data ? (
                        getRupeeWithDecimal.format(gstAmount)
                      ) : (
                        '-'
                      )}
                    </span>
                  </span>
                </div>
                <div className="p-4 md:p-5 flex items-center gap-4 text-green-700">
                  <h3 className="text-sm min-w-36">Your Commission</h3>
                  <span className="text-lg font-medium">
                    {partnerCommissionLoading ? (
                      <Skeleton width={100} animation="wave" />
                    ) : partnerCommission?.data ? (
                      fourDecimalNumberWithoutRounding(
                        partnerCommission?.data?.earnedIncomeForInvestment,
                      )
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
              </div>
            ) : (
              <div className="border border-stone-300 rounded-lg">
                {/* Order Investment Input */}
                <div className="border-b relative border-b-stone-300 text-slate-600 overflow-hidden h-32 bg-white rounded-t-lg">
                  <label className="absolute z-10 top-4 left-3 tracking-wider uppercase text-xs font-semibold text-black/40">
                    Investment {isGramSelected ? 'Quantity' : 'Amount'}
                  </label>
                  <div className="absolute z-10 top-10 left-2">
                    <div className=" flex items-center ">
                      {/* Input field */}
                      <Input
                        value={isGramSelected ? selectedGram : selectedAmount}
                        onChange={handleInputChange}
                        prefixIcon={
                          <span className="text-black/40 font-medium text-xl pt-3">
                            {isGramSelected ? 'gms' : '₹'}
                          </span>
                        }
                        longPrefix={isGramSelected}
                        onClick={() => {
                          track({
                            eventName: SILVER_EVENTS.ENTER_SILVER_AMOUNT,
                            eventProperties: {
                              amount: selectedAmount,
                              clientId: clientId,
                              partnerId: partnerId,
                            },
                          });
                        }}
                        onFocus={(e) => {
                          e.target.select();
                        }}
                        type="text"
                        placeholder={''}
                        className="font-semibold text-2xl placeholder:text-stone-400 pt-6 border-none rounded-b-none"
                        containerClassName="border-none rounded-b-none !shadow-none "
                      />

                      {/* Display calculated amount */}
                      <div className="flex items-center mr-4">
                        <span className="text-sm font-medium min-w-[80px] mr-2">
                          {metalLivePriceLoading ? (
                            <Skeleton width={100} animation="wave" />
                          ) : isGramSelected ? (
                            selectedAmount ? (
                              `₹${selectedAmount}`
                            ) : (
                              '-'
                            )
                          ) : selectedGram ? (
                            `${selectedGram} gms`
                          ) : (
                            '-'
                          )}
                        </span>

                        {/* Swap button */}
                        <button
                          onClick={handleSwap}
                          className="text-green-800 underline text-xs font-semibold"
                          type="button"
                          style={{
                            cursor: 'pointer',
                          }}
                          // disabled={
                          // selectedAmount === 0 || selectedGram === 0 || metalLivePriceLoading
                          // }
                        >
                          Swap
                        </button>
                      </div>
                    </div>

                    <div>
                      <button
                        type="button"
                        className={clsx(
                          'rounded-full py-1 px-2 font-semibold text-[10px] mt-2 ml-2',
                          client && client?.silverInGms
                            ? 'bg-slate-600 text-white'
                            : 'bg-stone-200 text-white',
                        )}
                        disabled={!client}
                        onClick={() => {
                          setSellMaxSilver(true);
                          setSelectedGram(client?.silverInGms);
                        }}
                      >
                        Sell Max
                      </button>
                    </div>
                  </div>

                  <div className="h-fit w-fit border border-stone-300 rounded-full text-red-700 absolute z-10 top-3 right-3 inset-y-0 text-xs flex px-2 py-[3px]">
                    Price valid for{' '}
                    {metalLivePrice?.data?.expiresAt ? (
                      <Typography
                        sx={{
                          fontSize: 'inherit',
                          fontWeight: 'inherit',
                        }}
                      >
                        <TimerComponent expiresAt={metalLivePrice?.data?.expiresAt} />
                      </Typography>
                    ) : (
                      <Skeleton animation="wave" width="50px" sx={{ ml: 1 }} />
                    )}
                  </div>
                </div>

                <div className="p-4 md:p-5 border-b border-b-stone-300 flex items-center gap-4 text-slate-600">
                  <h3 className="text-sm min-w-36">Live Price</h3>
                  <span className="text-lg font-medium line-clamp-1">
                    {metalLivePriceLoading ? (
                      <Skeleton width={100} animation="wave" />
                    ) : metalLivePrice?.data ? (
                      getRupeeWithDecimal.format(metalLivePrice?.data.silverSellPrice) + '/gm'
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
                <div className="p-4 md:p-5 flex items-center gap-4 text-slate-600">
                  <h3 className="text-sm min-w-36">Sell Price</h3>
                  <span className="text-lg font-medium">
                    <span className="text-lg font-medium">
                      {metalLivePriceLoading ? (
                        <Skeleton width={100} animation="wave" />
                      ) : metalLivePrice?.data ? (
                        getRupeeWithDecimal.format(selectedAmount)
                      ) : (
                        '-'
                      )}
                    </span>
                  </span>
                </div>
              </div>
            )}
          </div>

          {plan !== 'START_SIP' && (
            <>
              <div className="flex items-center my-2">
                <Checkbox
                  checked={isCheckboxSelected}
                  onClick={() => setIsCheckboxSelected(!isCheckboxSelected)}
                  sx={{
                    color: baseColors.green[500],
                    '& .MuiSvgIcon-root': {
                      fontSize: '18px',
                    },
                    paddingRight: '8px',
                    marginLeft: '-8px',

                    '&.Mui-checked': {
                      color: baseColors.green[700],
                    },
                  }}
                />
                <span className="text-sm text-stone-700 opacity-60 leading-5 width-[20ch]">
                  By proceeding you agree to our{' '}
                  <a
                    href="https://partner.tapinvest.in/terms"
                    className="underline underline-offset-4"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms & Conditions
                  </a>{' '}
                  involved.
                </span>
              </div>
              <div className="mt-6">
                <SelectClient
                  client={client}
                  setClient={setClient}
                  eventName={SILVER_EVENTS.SELECT_CLIENT}
                  showSilver={true}
                  refetchClients={fetchClients}
                />
              </div>
              {/* {!!client && client?.balance < parseInt(investmentAmount) &&
                        <p className="text-amber-700 mt-1.5 mb-2 text-sm font-semibold flex items-center gap-1"><InfoIcon fontSize="small" /> There's insufficient money in the client's wallet</p>
                    } */}
              <div className="mt-4 flex justify-between items-center gap-2.5">
                {/* {!!client && !isClientWalletEnough &&
                            <button
                                onClick={handleWalletOpen}
                                type="button"
                                className="bg-white shadow flex-shrink-0 flex items-center gap-2 text-sm text-gray-700 border border-gray-300 rounded-xl font-semibold h-10 w-fit px-4"
                            >
                                View Wallet Details
                            </button>
                        } */}
                {plan === 'SELL_SILVER' ? (
                  <KYCButton
                    text="Sell Silver"
                    isLoading={isInvestmentLoading}
                    className="!mt-0 h-10 !py-0 shadow"
                    style={{
                      boxShadow: '0px 1.1px 1.8px -0.3px rgba(0, 0, 0, 0.10)',
                      background:
                        !clientId || !partnerId || !selectedAmount || !client || client.metalBlocked
                          ? '#E0E0E0'
                          : 'linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.00) 119.05%), #1E293B',
                      color:
                        !clientId || !partnerId || !selectedAmount || !client || client.metalBlocked
                          ? '#9E9E9E'
                          : '#FFFFFF',
                      opacity:
                        !clientId || !partnerId || !selectedAmount || !client || client.metalBlocked
                          ? 0.7
                          : 1,
                      cursor:
                        !clientId || !partnerId || !selectedAmount || !client || client.metalBlocked
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                    disabled={
                      !clientId || !partnerId || !selectedAmount || !client || client.metalBlocked
                    }
                  />
                ) : (
                  <KYCButton
                    text="Generate Payment Link"
                    isLoading={isInvestmentLoading}
                    className="!mt-0 h-10 !py-0 shadow"
                    style={{
                      boxShadow: '0px 1.1px 1.8px -0.3px rgba(0, 0, 0, 0.10)',
                      background:
                        !clientId ||
                        !partnerId ||
                        !isCheckboxSelected ||
                        !selectedAmount ||
                        !client ||
                        client.metalBlocked
                          ? '#E0E0E0'
                          : 'linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.00) 119.05%), #1E293B',
                      color:
                        !clientId ||
                        !partnerId ||
                        !isCheckboxSelected ||
                        !selectedAmount ||
                        !client ||
                        client.metalBlocked
                          ? '#9E9E9E'
                          : '#FFFFFF',
                      opacity:
                        !clientId ||
                        !partnerId ||
                        !isCheckboxSelected ||
                        !selectedAmount ||
                        !client ||
                        client.metalBlocked
                          ? 0.7
                          : 1,
                      cursor:
                        !clientId ||
                        !partnerId ||
                        !isCheckboxSelected ||
                        !selectedAmount ||
                        !client ||
                        client.metalBlocked
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                    disabled={
                      !clientId ||
                      !partnerId ||
                      !isCheckboxSelected ||
                      !selectedAmount ||
                      !client ||
                      client.metalBlocked
                    }
                  />
                )}
              </div>
              <MessageCycler messages={messages} />
              {/* FAQs */}
              <div className="mt-6 flex flex-row gap-1 items-baseline">
                <h3 className="text-black/40 text-sm font-medium">Have a question?</h3>
                <a
                  href="/faq?tab=SILVER"
                  className="underline underline-offset-2 text-base text-stone-600 "
                >
                  Checkout FAQ's
                </a>
              </div>
            </>
          )}
        </form>
      </div>

      {/* Client Generate Payment Link */}
      <Dialog open={isPaymentOpen} maxWidth="sm" fullWidth>
        <div className="flex bg-slate-100 p-2.5 items-center gap-2 ">
          <InfoIcon
            className="text-slate-700"
            sx={{
              height: '16px',
              width: '16px',
            }}
          />
          <h4 className="text-sm text-slate-700 font-medium">
            Price will be changed in the next 5 minutes
          </h4>
        </div>
        <DialogTitle>
          <div className="px-5 flex justify-between">
            <div>
              <h3 className="text-lg font-semibold">Link Generated</h3>
              <p className="text-slate-600 text-sm mt-1.5">
                Payment link is valid for 6 hours, however price will change every 5 minutes.
              </p>
            </div>
            <CloseOutlinedIcon
              className="cursor-pointer bg-slate-200 rounded-full p-1"
              onClick={() => {
                handlePaymentClose();
                setPlan('START_SIP');
                setSelectedSipFrequency(null);
              }}
              style={{
                width: '28px',
                height: '28px',
              }}
            />
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="py-5bg-white rounded-lg">
            <div className="p-5 pb-3 flex gap-3">
              <div
                className="flex flex-col justify-between gap-[6px]  flex-1"
                style={{
                  padding: '14px 16px 16px 0px',
                  borderRight: '1px solid #f0f0f0',
                }}
              >
                <h4 className="text-[10px] text-stone-400 ">INVESTMENT FOR</h4>
                <h3 className="text-sm font-medium">{client?.name || '-'}</h3>
              </div>
              <div
                className="flex flex-col justify-between gap-[6px] flex-1"
                style={{
                  padding: '14px 16px 16px 0px',
                  borderRight: '1px solid #f0f0f0',
                }}
              >
                <h4 className="text-[10px] text-stone-400">INVESTMENT IN RUPEES</h4>
                <h3 className="text-sm font-medium">
                  {isSipPayment
                    ? `₹${sipPaymentData?.amount?.toLocaleString() || '0'}`
                    : getRupeeWithDecimal.format(gstAmount)}
                </h3>
              </div>
              {isSipPayment ? (
                <>
                  <div
                    className="flex flex-col justify-between gap-[6px] flex-1"
                    style={{
                      padding: '14px 16px 16px 0px',
                      borderRight: '1px solid #f0f0f0',
                    }}
                  >
                    <h4 className="text-[10px] text-stone-400">AUTO DEBIT</h4>
                    <h3 className="text-sm font-medium">
                      {sipPaymentData?.frequency === 'DAILY'
                        ? 'Everyday'
                        : sipPaymentData?.frequency === 'WEEKLY'
                          ? `Weekly on ${sipPaymentData?.selectedDay ?? '—'}`
                          : `On every ${sipPaymentData?.selectedDate ? formatMonthlyDate(sipPaymentData.selectedDate) : '—'}`}
                    </h3>
                  </div>
                  <div
                    className="flex flex-col justify-between gap-[6px] flex-1"
                    style={{ padding: '14px 16px 16px 24px' }}
                  >
                    <h4 className="text-[10px] text-stone-400">VALIDITY</h4>
                    <h3 className="text-sm font-medium">Until Cancelled</h3>
                  </div>
                </>
              ) : (
                <div
                  className="flex flex-col justify-between gap-[6px] flex-1"
                  style={{ padding: '14px 16px 16px 24px' }}
                >
                  <h4 className="text-[10px] text-stone-400">INVESTMENT IN GRAMS</h4>
                  <h3 className="text-sm font-medium">
                    {fourDecimalNumberWithoutRounding(selectedGram) + 'gms'}
                  </h3>
                </div>
              )}
            </div>
            <div className="px-5 pt-6 pb-3">
              <div className="flex flex-col gap-[6px]">
                <h4 className="text-sm text-gray-600">Share link with your client</h4>
                <div className="flex items-center w-full">
                  <input
                    type="text"
                    value={`${userPaymentLink || 'loading link...'}`}
                    readOnly
                    className="flex-grow border border-gray-300 rounded-l-xl p-2"
                  />
                  <button
                    // onClick={copyToClipboard}
                    onClick={() => {
                      navigator.clipboard.writeText(userPaymentLink);
                      toast.success('Link copied to clipboard', {
                        duration: 2000,
                        position: 'top-center',
                      });
                    }}
                    className="bg-green-700 text-white text-sm font-semibold h-[2.65rem] px-4 rounded-r-xl "
                  >
                    Copy Url
                  </button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SellMetalOtpDialog
        open={isSellPaymentOpen}
        onClose={handleSellDialogClose}
        client={client}
        metalType="SILVER"
        metalLabel="Silver"
        quantity={selectedGram}
        amount={selectedAmount}
        sellPrice={Number(metalLivePrice?.data?.silverSellPrice) || 0}
        blockId={metalLivePrice?.data?.blockId}
        onSuccess={handleSellSuccess}
      />

      <ClientDrawer />
    </div>
  );
};

export default DigitalSilver;
